import React, { useState } from 'react';
import { portfolios } from './data/portfolios';
import PortfolioInput from './components/PortfolioInput';
import PortfolioSummary from './components/PortfolioSummary';
import CombinedSummary from './components/CombinedSummary';
import ErrorBoundary from './components/ErrorBoundary';
import WelcomeScreen from './components/WelcomeScreen';
import InvestmentDistributionChart from './components/InvestmentDistributionChart';

function App() {
  const [investmentAmounts, setInvestmentAmounts] = useState([0, 0, 0, 0]);
  const [userEmail, setUserEmail] = useState('');
  const [showCalculator, setShowCalculator] = useState(false);

  const handleInvestmentChange = (portfolioId, amount) => {
    try {
      const newAmounts = [...investmentAmounts];
      const index = portfolioId - 1;
      if (index >= 0 && index < newAmounts.length) {
        newAmounts[index] = amount || 0;
        setInvestmentAmounts(newAmounts);
      }
    } catch (error) {
      console.error('Error updating investment amounts:', error);
    }
  };

  const handleWelcomeContinue = (email) => {
    setUserEmail(email);
    setShowCalculator(true);
  };

  // Validate portfolios data
  const validPortfolios = portfolios && Array.isArray(portfolios) ? portfolios : [];

  // Prepare data for the distribution chart
  const portfolioInvestments = validPortfolios.map((portfolio, index) => ({
    name: portfolio.name,
    investmentAmount: investmentAmounts[index] || 0
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-dark-blue shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="/images/ppal-claro.png" 
                alt="Nee'k Logo" 
                className="h-12 w-auto mr-4"
              />
              <div>
                <h1 className="text-2xl font-bold text-gold">Bienvenido a la Calculadora Nee'k</h1>
                <p className="text-sm text-gray-300">Simula los rendimientos de tu inversión</p>
              </div>
            </div>
            {showCalculator && (
              <div className="text-right">
                <p className="text-sm text-gray-300">Bienvenido</p>
                <p className="text-gold font-medium">{userEmail}</p>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      {!showCalculator && (
        <WelcomeScreen onContinue={handleWelcomeContinue} />
      )}

      <div className={!showCalculator ? 'filter blur-sm pointer-events-none' : ''}>
        <ErrorBoundary>
          <main className="container mx-auto px-4 py-6">
            {/* Investment Inputs */}
            <section className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-medium text-[#0c1230]">Portafolios de Inversión</h2>
              </div>
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  {validPortfolios.map((portfolio) => (
                    <ErrorBoundary key={portfolio.id || Math.random()}>
                      <div className="mb-2 last:mb-0">
                        <PortfolioInput
                          portfolio={portfolio}
                          investmentAmount={investmentAmounts[portfolio.id - 1] || 0}
                          onInvestmentChange={handleInvestmentChange}
                        />
                      </div>
                    </ErrorBoundary>
                  ))}
                </div>
                <div className="flex-1 min-h-[220px]">
                  <InvestmentDistributionChart portfolioInvestments={portfolioInvestments} />
                </div>
              </div>
            </section>

            {/* Portfolio Summaries */}
            <section className="mb-6">
              <div className="space-y-2">
                {validPortfolios.map((portfolio) => (
                  <ErrorBoundary key={portfolio.id || Math.random()}>
                    <PortfolioSummary
                      portfolio={portfolio}
                      investmentAmount={investmentAmounts[portfolio.id - 1] || 0}
                    />
                  </ErrorBoundary>
                ))}
              </div>
            </section>

            {/* Combined Summary */}
            <section>
              <ErrorBoundary>
                <CombinedSummary
                  portfolios={validPortfolios}
                  investments={Object.fromEntries(validPortfolios.map((p, i) => [p.id, investmentAmounts[i] || 0]))}
                />
              </ErrorBoundary>
            </section>
          </main>
        </ErrorBoundary>
      </div>

      {/* Footer */}
      <footer className="bg-dark-blue text-white py-6 mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-2 text-center">
            <span className="text-gray-400">Respaldado por</span>
            <img 
              src="/images/ahalfinancial.png" 
              alt="Nee'k Logo" 
              className="h-12 w-auto"
            />
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;